import { useEffect, useMemo, useState } from 'react';
import { PageHead } from '../../components/ui';
import {
  bucketTotals,
  costSummaryText,
  priorityRank,
  topPriority,
} from '../../lib/amCosts';
import { formatDate, formatNumber } from '../../lib/format';
import type { AmClientDetail } from '../../lib/types';
import { CostCell, PriorityBadge, SendPanel, SignatureBlock } from './amUi';

/**
 * IT recommendations proposal.
 *
 * The counterpart to the risk acceptance form: that one records what the client
 * declined, this one asks them to approve. Same shape deliberately — pick the
 * items, print the document, send the summary — because they are two halves of
 * the same conversation and an account manager should not have to learn two
 * different screens for it.
 */
export function AmProposal({
  client,
  organizationName,
  onBack,
}: {
  client: AmClientDetail;
  organizationName: string;
  onBack: () => void;
}) {
  const sorted = useMemo(
    () =>
      [...client.recommendations].sort(
        (a, b) => priorityRank(topPriority(b)) - priorityRank(topPriority(a)),
      ),
    [client.recommendations],
  );

  /* Proposed items start ticked — those are the ones not yet decided, which is
     what a proposal is for. Approved and declined work is already settled. */
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(sorted.filter((r) => r.Status === 'Proposed').map((r) => r.AmRecommendationId)),
  );
  const [recipient, setRecipient] = useState('');

  useEffect(() => {
    const decisionMaker = client.stakeholders.find((s) => s.Email);
    setRecipient(decisionMaker?.Email ?? client.Email ?? '');
  }, [client]);

  const items = sorted.filter((r) => selected.has(r.AmRecommendationId));
  const totals = bucketTotals(items);
  const today = formatDate(new Date().toISOString());

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalLines = [
    totals.oneTime > 0 && `Total one-time investment: ${formatNumber(totals.oneTime, 'currency')}`,
    totals.monthly > 0 &&
      `Total monthly investment: ${formatNumber(totals.monthly, 'currency')} (${formatNumber(totals.monthly * 12, 'currency')} a year)`,
    totals.annual > 0 && `Total annual investment: ${formatNumber(totals.annual, 'currency')}`,
  ].filter((l): l is string => Boolean(l));

  const emailBody = [
    `Dear ${client.PrimaryContact ?? client.Name},`,
    '',
    `Please find attached our IT recommendations proposal for ${client.Name}, prepared on ${today}. It contains ${items.length} recommendation${items.length === 1 ? '' : 's'}, summarised below:`,
    '',
    ...items.map(
      (r, n) =>
        `${n + 1}. ${r.Title ?? 'Recommendation'} — Priority: ${topPriority(r)} — Investment: ${costSummaryText(r)}`,
    ),
    '',
    ...totalLines,
    '',
    'Please review the attached proposal, indicate your decision for each recommendation, and return a signed copy. We would be glad to walk through any of these with you.',
    '',
    'Kind regards,',
    organizationName,
  ].join('\n');

  const copyText = () => {
    const lines = [
      'IT RECOMMENDATIONS PROPOSAL',
      `${organizationName} · Prepared for ${client.Name} · ${today}`,
      '',
      'RECOMMENDATIONS',
    ];
    items.forEach((r, n) => {
      lines.push(
        `${n + 1}. ${r.Title ?? 'Recommendation'} — Priority: ${topPriority(r)} — Investment: ${costSummaryText(r)}`,
      );
      if (r.Finding) lines.push(`   Finding: ${r.Finding}`);
      if (r.Description) lines.push(`   Scope: ${r.Description}`);
      if (r.Effort) lines.push(`   Effort / timeline: ${r.Effort}`);
      lines.push('   Decision:  [ ] Approve   [ ] Decline   [ ] Discuss');
    });
    lines.push(
      '',
      ...totalLines,
      '',
      'PURPOSE',
      `${organizationName} has assessed ${client.Name}'s IT environment and recommends the initiatives above to strengthen security, reliability and operational efficiency. Each recommendation addresses a finding identified during our reviews.`,
      '',
      'APPROVAL',
      `We have reviewed the recommendations above and indicated our decision for each. Approved items authorise ${organizationName} to proceed with scheduling and implementation at the investment shown.`,
      '',
      'AUTHORISED REPRESENTATIVE',
      'Name: ______________________   Title: ______________________',
      `Company: ${client.Name}`,
      'Signature: ___________________   Date: _______________________',
    );
    return lines.join('\n');
  };

  return (
    <>
      <div className="no-print">
        <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
          ← Back to the client
        </button>
        <PageHead
          title="IT recommendations proposal"
          lede="Put the recommendations in front of the client as one costed document, with a decision box against each."
        />

        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Step one</span>
              <h2>Choose the recommendations</h2>
              <p className="sub">
                {items.length} of {sorted.length} included
              </p>
            </div>
          </div>
          <div className="card-body stack" style={{ gap: 'var(--space-2)' }}>
            {sorted.length === 0 ? (
              <p className="small muted">
                Nothing to propose yet. Add recommendations on the client, or raise them from an
                audit finding.
              </p>
            ) : (
              sorted.map((r) => (
                <label key={r.AmRecommendationId} className="checkbox">
                  <input
                    type="checkbox"
                    checked={selected.has(r.AmRecommendationId)}
                    onChange={() => toggle(r.AmRecommendationId)}
                  />
                  <PriorityBadge priority={topPriority(r)} />
                  <span className="grow small strong">{r.Title}</span>
                  <span className="xs muted">{costSummaryText(r)}</span>
                  <span className="xs muted">{r.Status}</span>
                </label>
              ))
            )}
          </div>
        </section>

        <SendPanel
          recipient={recipient}
          onRecipient={setRecipient}
          suggestions={[
            ...client.stakeholders
              .filter((s) => s.Email)
              .map((s) => ({
                value: s.Email as string,
                label: `${s.Name} (${s.Role ?? 'stakeholder'})`,
              })),
            ...(client.Email
              ? [{ value: client.Email, label: client.PrimaryContact ?? client.Name }]
              : []),
          ]}
          subject={`IT recommendations proposal — ${client.Name}`}
          body={emailBody}
          disabled={items.length === 0}
          copyLabel="Copy proposal text"
          copyText={copyText}
        />
      </div>

      {/* ---------- the printable document ---------- */}
      <article className="am-doc">
        <header className="am-doc-head">
          <div>
            <div className="am-doc-brand">{organizationName}</div>
            <h2>IT recommendations proposal</h2>
          </div>
          <div className="am-doc-meta">
            <div>
              <strong>Prepared for:</strong> {client.Name}
            </div>
            <div>
              <strong>Date:</strong> {today}
            </div>
            {items.length > 0 && (
              <div>
                <strong>Recommendations:</strong> {items.length}
              </div>
            )}
          </div>
        </header>

        <div className="am-doc-title">Purpose</div>
        <p>
          {organizationName} has assessed {client.Name}&rsquo;s IT environment and recommends the
          initiatives below to strengthen security, reliability and operational efficiency. Each one
          addresses a finding identified during our reviews, with the associated investment and
          effort shown for your approval.
        </p>

        <div className="am-doc-title">Recommendations</div>
        {items.length === 0 ? (
          <p className="muted small">No recommendations selected.</p>
        ) : (
          <table className="am-doc-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>#</th>
                <th>Recommendation</th>
                <th style={{ width: 90 }}>Priority</th>
                <th style={{ width: 120 }}>Investment</th>
                <th style={{ width: '22%' }}>Client decision</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, n) => (
                <tr key={r.AmRecommendationId}>
                  <td>{n + 1}</td>
                  <td>
                    <div className="strong">{r.Title}</div>
                    {r.Finding && <div className="xs muted">Finding: {r.Finding}</div>}
                    {r.Description && <div className="xs muted">Scope: {r.Description}</div>}
                    {r.Effort && <div className="xs muted">Effort: {r.Effort}</div>}
                  </td>
                  <td>
                    <PriorityBadge priority={topPriority(r)} />
                  </td>
                  <td className="num">
                    <CostCell rec={r} />
                  </td>
                  <td className="am-decision">
                    <div>☐ Approve</div>
                    <div>☐ Decline</div>
                    <div>☐ Discuss</div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {totals.oneTime > 0 && (
                <tr>
                  <td colSpan={3} className="num">
                    <strong>Total one-time investment</strong>
                  </td>
                  <td className="num">
                    <strong>{formatNumber(totals.oneTime, 'currency')}</strong>
                  </td>
                  <td />
                </tr>
              )}
              {totals.monthly > 0 && (
                <tr>
                  <td colSpan={3} className="num">
                    <strong>Total monthly investment</strong>
                  </td>
                  <td className="num">
                    <strong>{formatNumber(totals.monthly, 'currency')}</strong>
                    <div className="xs muted">{formatNumber(totals.monthly * 12, 'currency')} a year</div>
                  </td>
                  <td />
                </tr>
              )}
              {totals.annual > 0 && (
                <tr>
                  <td colSpan={3} className="num">
                    <strong>Total annual investment</strong>
                  </td>
                  <td className="num">
                    <strong>{formatNumber(totals.annual, 'currency')}</strong>
                  </td>
                  <td />
                </tr>
              )}
            </tfoot>
          </table>
        )}

        <div className="am-doc-title">Approval</div>
        <p>
          We have reviewed the recommendations above and indicated our decision for each. Approved
          items authorise {organizationName} to proceed with scheduling and implementation at the
          investment shown. Declined items may be documented separately in a security risk
          acceptance form where applicable.
        </p>

        <SignatureBlock companyName={client.Name} />
      </article>
    </>
  );
}
